import React, { useState, useRef, useEffect } from 'react';

/**
 * Enhanced lazy loading image component with intersection observer and fallback support
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text for accessibility
 * @param {string} [props.className] - CSS classes
 * @param {Object} [props.style] - Inline styles
 * @param {Function} [props.onLoad] - Load event handler
 * @param {Function} [props.onError] - Error event handler
 * @param {string} [props.placeholder] - Placeholder image URL
 * @param {boolean} [props.enableIntersectionObserver=true] - Use intersection observer for lazy loading
 * @param {number} [props.rootMargin=50] - Root margin for intersection observer (pixels)
 * @param {boolean} [props.eager=false] - Load image immediately (for critical images)
 * @param {boolean} [props.isGalleryImage=false] - Special handling for gallery images
 * @param {boolean} [props.enableFallback=true] - Enable fallback to production URL on error
 * @param {string} [props.fallbackSrc] - Custom fallback URL (defaults to production URL)
 * @returns {React.ReactElement} Lazy loaded image component
 */
const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  style, 
  onLoad, 
  onError,
  placeholder,
  enableIntersectionObserver = true,
  rootMargin = 50,
  eager = false,
  isGalleryImage = false,
  enableFallback = true,
  fallbackSrc
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(!enableIntersectionObserver || eager);
  const [imageSrc, setImageSrc] = useState(placeholder || (eager ? src : null));
  const [hasFallback, setHasFallback] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Generate fallback URL if not provided
  const productionFallback = fallbackSrc || (src?.startsWith('/') ? `https://marcodemarco.net${src}` : null);

  // For gallery images, use more aggressive loading settings
  const effectiveRootMargin = isGalleryImage ? Math.max(rootMargin, 200) : rootMargin;
  const effectiveThreshold = isGalleryImage ? 0.01 : 0.1;

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!enableIntersectionObserver || eager || !imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          setImageSrc(src);
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin: `${effectiveRootMargin}px`,
        threshold: effectiveThreshold
      }
    );

    observer.observe(imgRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, enableIntersectionObserver, effectiveRootMargin, effectiveThreshold, eager]);

  // Set image source immediately if intersection observer is disabled or eager loading
  useEffect(() => {
    if (!enableIntersectionObserver || eager) {
      setImageSrc(src);
      setIsInView(true);
    }
  }, [src, enableIntersectionObserver, eager]);

  const handleLoad = (e) => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.(e);
  };

  const handleError = (e) => {
    setIsLoading(false);
    
    // Try fallback URL if enabled and available
    if (enableFallback && productionFallback && !hasFallback && imageSrc !== productionFallback) {
      console.log(`Local image failed, trying production fallback: ${productionFallback}`);
      setHasFallback(true);
      setImageSrc(productionFallback);
      setIsLoading(true); // Reset loading state for fallback attempt
      return;
    }
    
    // If fallback also failed or no fallback available
    setHasError(true);
    onError?.(e);
  };

  // Error state
  if (hasError) {
    return (
      <div className={`image-error-placeholder ${className}`} style={style}>
        <i className="fas fa-image"></i>
        <span>Image unavailable</span>
        {hasFallback && (
          <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
            Fallback also failed
          </small>
        )}
      </div>
    );
  }

  // Loading skeleton while image is not in view or loading
  if (!isInView || (isLoading && !placeholder && !imageSrc)) {
    return (
      <div 
        ref={imgRef}
        className={`image-placeholder ${className}`} 
        style={style}
      >
        <div className="loading-skeleton"></div>
      </div>
    );
  }

  return (
    <div className="lazy-image-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        className={`lazy-image ${isLoading ? 'loading' : 'loaded'} ${hasFallback ? 'fallback-image' : ''} ${className}`}
        style={style}
        onLoad={handleLoad}
        onError={handleError}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
      />
      {hasFallback && !hasError && !isLoading && (
        <div 
          className="fallback-indicator" 
          title="Loaded from production server"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(0, 123, 255, 0.8)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '10px',
            fontSize: '0.7em',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        >
          <i className="fas fa-cloud" style={{ marginRight: '2px' }}></i>
          CDN
        </div>
      )}
    </div>
  );
};

export default LazyImage;
